/**
 * ApiService.ts
 *
 * A centralized service for making API requests to the Django backend.
 * This replaces the mock implementation that used IndexedDB.
 */

import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';
import { Appointment } from '../types/appointment';
import { Medication } from '../types/medication';
import { Patient } from '../types/patient';
import { TreatmentPlan } from '../types/treatmentPlan';
import { store, persistor } from '../redux/store';
import { setTokens } from '../redux/slices/authSlice';

// Base URL for Django backend
const API_BASE_URL = import.meta.env.VITE_APP_API_URL;

// Storage keys for auth tokens
const TOKEN_KEY = 'auth_token';
const REFRESH_TOKEN_KEY = 'refresh_token';

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

// Create axios instance
const axiosInstance: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  // Do not send cookies with cross-site requests to avoid CORS issues
  withCredentials: false,
});

// Add interceptor to include auth token in requests
axiosInstance.interceptors.request.use(
  config => {
    const token = store.getState().auth.tokens?.access;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (token && config.headers) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// Add interceptor to handle token refresh on 401 responses
axiosInstance.interceptors.response.use(
  response => response,
  async (error: AxiosError) => {
    const originalRequest: any = error.config;

    // If error response is 401 and we haven't already tried to refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      console.log('Token expired, attempting refresh');

      try {
        // Try to refresh the token
        const refreshToken = store.getState().auth.tokens?.refresh;
        if (!refreshToken) {
          throw new Error('No refresh token available');
        }

        // Use a simple axios config for token refresh to avoid CORS issues
        const response = await axios({
          method: 'POST',
          url: `${API_BASE_URL}/api/token/refresh/`,
          data: { refresh: refreshToken },
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          withCredentials: false,
        });

        console.log('Token refresh successful');
        const newToken = response.data.access;
        store.dispatch(setTokens({ access: newToken, refresh: refreshToken }));

        // Retry the original request with the new token
        if (originalRequest.headers) {
          originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
        } else {
          originalRequest.headers = { Authorization: `Bearer ${newToken}` };
        }

        // Ensure we're using the same simplified approach for the retry
        return axios({
          ...originalRequest,
          withCredentials: false,
        });
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError);
        // Clear all persisted storage
        await persistor.purge();
        store.dispatch(setTokens({ access: '', refresh: '' }));
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Authentication class - handles login, logout, token refresh
export class AuthApi {
  static async login(
    email: string,
    password: string
  ): Promise<ApiResponse<{ token: string; user: any }>> {
    try {
      console.log('Attempting login with email:', email);

      // Create a plain axios instance without any interceptors for this specific request
      // This helps avoid any CORS preflight issues
      const loginResponse = await axios({
        method: 'POST',
        url: `${API_BASE_URL}/api/token/`,
        data: { email, password },
        headers: {
          // Simple headers to avoid preflight
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        // Ensure we're not sending credentials that trigger preflight
        withCredentials: false,
      });

      console.log('Login response status:', loginResponse.status);

      // Save tokens
      const tokens = loginResponse.data;
      store.dispatch(setTokens(tokens));

      console.log('Tokens saved, trying to get user info');

      // Try to get user info
      try {
        const userResponse = await axiosInstance.get('/current-user/');
        return {
          success: true,
          data: {
            token: tokens.access,
            user: userResponse.data,
          },
        };
      } catch (userError) {
        // If we can't get user info but have token, return partial success
        console.warn('Could not get user profile, but authentication succeeded');
        return {
          success: true,
          data: {
            token: tokens.access,
            user: { email }, // Return minimal user info
          },
        };
      }
    } catch (error) {
      console.error('Login error:', error);
      let message = 'Login failed';

      if (axios.isAxiosError(error)) {
        console.error('Axios error details:', {
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
          headers: error.response?.headers,
          config: {
            url: error.config?.url,
            method: error.config?.method,
            headers: error.config?.headers,
          },
        });

        message =
          error.response?.data?.detail || error.response?.data?.message || 'Authentication failed';
      }

      return {
        success: false,
        error: message,
      };
    }
  }
}

// Base API class
class BaseApi {
  static async request<T = any>(
    endpoint: string,
    method: string = 'GET',
    data?: any
  ): Promise<ApiResponse<T>> {
    try {
      const config: AxiosRequestConfig = {
        method,
        url: endpoint,
        // Ensure CORS settings are consistent
        withCredentials: false,
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      };

      if (data && ['POST', 'PUT', 'PATCH'].includes(method)) {
        config.data = data;
      }

      const response = await axiosInstance(config);
      return { success: true, data: response.data };
    } catch (error) {
      console.error(`API ${method} error for ${endpoint}:`, error);

      let errorMessage = 'API request failed';
      if (axios.isAxiosError(error)) {
        console.error('Axios error details:', {
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
          headers: error.response?.headers,
          config: {
            url: error.config?.url,
            method: error.config?.method,
            headers: error.config?.headers,
          },
        });

        errorMessage =
          error.response?.data?.detail || error.response?.data?.message || error.message;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      return { success: false, error: errorMessage };
    }
  }
}

// Patient API - handles patient data
export class PatientApi extends BaseApi {
  static async getAll(): Promise<ApiResponse<Patient[]>> {
    const response = await this.request<any>('/patients/');

    if (response.success && response.data) {
      // Check the response shape to match the backend API structure
      // Handle both paginated and non-paginated responses
      const patientArray = response.data.results || response.data;

      try {
        // Based on the API response format [{"id":1,"first_name":"Jane","last_name":"Smith",...}]
        const formattedPatients: Patient[] = patientArray.map((item: any, index: number) => {
          // Extract patient details from the updated API response format
          return {
            id: item.id ? String(item.id) : `temp-${index}`,
            firstName: item.first_name || '',
            lastName: item.last_name || '',
            email: item.email || '',
            phone: item.phone || '',
            dateOfBirth: item.dob || '',
            address: item.address || '',
            medicareNumber: '', // Not included in the response
            condition: item.occupation || '', // Using occupation as condition temporarily
            status: 'active', // Default status
            progress: 0, // Default progress
            lastVisit: 'Not available', // Default lastVisit
            dateAdded: new Date().toISOString().split('T')[0], // Default dateAdded
            notes: item.referred_by ? `Referred by: ${item.referred_by}` : '',
            currentTreatmentPlan: '',
          };
        });

        return { success: true, data: formattedPatients };
      } catch (error) {
        console.error('Error transforming patient data:', error);
        // Return an empty array rather than failing
        return { success: true, data: [] };
      }
    }

    return response;
  }

  static async getById(id: string): Promise<ApiResponse<Patient>> {
    try {
      const response = await this.request<any>(`/patients/${id}/`);

      if (response.success && response.data) {
        // The detail API might return a different format than the list API
        // We need to handle both potential formats
        const backendPatient = response.data;

        // Handle the case where we get a patient object
        if (backendPatient) {
          let firstName = '';
          let lastName = '';
          let email = '';
          let phone = '';
          let dateOfBirth = '';
          let address = '';
          let medicareNumber = '';
          let condition = '';
          let notes = '';

          // Try to extract user info
          if (backendPatient.user) {
            // User info is available directly
            firstName = backendPatient.user.first_name || '';
            lastName = backendPatient.user.last_name || '';
            email = backendPatient.user.email || '';
            phone = backendPatient.user.phone || '';
            dateOfBirth = backendPatient.user.dob || '';
            address = backendPatient.user.address || '';
          } else if (backendPatient.patient_name) {
            // Just a name string is available
            const nameParts = backendPatient.patient_name.split(' ');
            firstName = nameParts[0] || '';
            lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : '';
          }

          // Try to extract medical info
          if (backendPatient.medical_info) {
            medicareNumber = backendPatient.medical_info.medicare || '';
            condition = backendPatient.medical_info.cur_medications || '';
            notes = backendPatient.medical_info.medical_history || '';
          }

          // Calculate progress from treatment plans if available
          let progress = 0;
          if (backendPatient.treatment_plans && backendPatient.treatment_plans.length > 0) {
            const firstPlan = backendPatient.treatment_plans[0];
            if (firstPlan.completion) {
              const [completed, total] = firstPlan.completion.split('/').map(Number);
              progress = total > 0 ? Math.round((completed / total) * 100) : 0;
            }
          }

          const patient: Patient = {
            id: id, // Use the ID we already have
            firstName,
            lastName,
            email,
            phone,
            dateOfBirth,
            address,
            medicareNumber,
            condition,
            status:
              backendPatient.patient_status ||
              backendPatient.doctorpatient__active_status ||
              'active',
            progress: progress,
            lastVisit: backendPatient.last_activity_date
              ? new Date(backendPatient.last_activity_date).toLocaleDateString()
              : '',
            dateAdded: backendPatient.create_time || new Date().toISOString(),
            notes,
            currentTreatmentPlan: backendPatient.treatment_plans?.[0]?.id?.toString(),
          };

          console.log('Transformed patient detail:', patient);
          return { success: true, data: patient };
        }
      }

      return { success: false, error: 'Patient not found or invalid format' };
    } catch (error) {
      console.error('Error fetching patient by ID:', error);
      return { success: false, error: 'Failed to fetch patient details' };
    }
  }

  static async create(patient: Omit<Patient, 'id'>): Promise<ApiResponse<{ id: string }>> {
    let formattedDob = patient.dateOfBirth;
    if (patient.dateOfBirth) {
      try {
        // Try to create a date object and format it to YYYY-MM-DD
        const date = new Date(patient.dateOfBirth);
        formattedDob = date.toISOString().split('T')[0]; // Gets YYYY-MM-DD format
      } catch (error) {
        console.error('Error formatting date of birth:', error);
        // If there's an error, keep the original value
      }
    }

    // Transform frontend patient to match Django backend format
    const backendPatient = {
      occupation: patient.notes || '',
      referred_by: '',
      language: 'EN', // Default to English
      communication_preference: 'EMAIL', // Default to Email
      user: {
        first_name: patient.firstName,
        last_name: patient.lastName,
        email: patient.email,
        phone: patient.phone,
        dob: formattedDob,
        address: patient.address,
      },
      medical_info: {
        medicare: patient.medicareNumber || '0',
        cur_medications: patient.condition || '',
        medical_history: patient.notes || '',
        prev_treatments: '',
        emergency_contact: '',
        patient_relation: '',
        emergency_phone: '',
        insurance_provider: '',
        insurance_number: 0,
        insurance_group: '',
        document_loc: '',
        allergies: '',
      },
    };

    return this.request<{ id: string }>('/patients/', 'POST', backendPatient);
  }

  static async update(id: string, patient: Partial<Patient>): Promise<ApiResponse<void>> {
    // Transform frontend patient to match Django backend format
    const backendPatient: any = {};

    if (
      patient.firstName ||
      patient.lastName ||
      patient.email ||
      patient.phone ||
      patient.dateOfBirth ||
      patient.address
    ) {
      backendPatient.user = {};
      if (patient.firstName) backendPatient.user.first_name = patient.firstName;
      if (patient.lastName) backendPatient.user.last_name = patient.lastName;
      if (patient.email) backendPatient.user.email = patient.email;
      if (patient.phone) backendPatient.user.phone = patient.phone;
      if (patient.dateOfBirth) backendPatient.user.dob = patient.dateOfBirth;
      if (patient.address) backendPatient.user.address = patient.address;
    }

    if (patient.medicareNumber || patient.condition || patient.notes) {
      backendPatient.medical_info = {};
      if (patient.medicareNumber) backendPatient.medical_info.medicare = patient.medicareNumber;
      if (patient.condition) backendPatient.medical_info.cur_medications = patient.condition;
      if (patient.notes) backendPatient.medical_info.medical_history = patient.notes;
    }

    return this.request<void>(`/patients/${id}/`, 'PATCH', backendPatient);
  }

  static async delete(id: string): Promise<ApiResponse<void>> {
    return this.request<void>(`/patients/${id}/`, 'DELETE');
  }
}

// Treatment Plan API
export class TreatmentPlanApi extends BaseApi {
  static async getAll(folderId?: string): Promise<ApiResponse<any>> {
    // The backend expects a folder_id query parameter to filter by folder
    const endpoint = folderId ? `/treatment-plans/?folder_id=${folderId}` : '/treatment-plans/';
    const response = await this.request<any>(endpoint);

    if (response.success && response.data) {
      // Check if the response is already in the expected format with id and children
      if (response.data.id && response.data.children) {
        console.log('API already returned hierarchical structure:', response.data);
        return {
          success: true,
          data: response.data,
        };
      }

      // If it's the format with 'folders' and 'treatments' properties
      if (response.data.folders !== undefined || response.data.treatments !== undefined) {
        const { folders = [], treatments = [] } = response.data;

        console.log('Backend treatment plan response:', {
          folders,
          treatments,
        });

        // For the TreatmentTemplates component, we need to create a hierarchical structure
        // that mimics the /folders/ endpoint response format
        const rootFolder = {
          id: 2, // Using the root ID from your example
          name: 'Treatments',
          type: 'folder',
          children: [
            // Add folders first (for proper sorting)
            ...folders.map((folder: any) => ({
              id: folder.id,
              name: folder.name || '',
              type: 'folder',
              children: folder.children || [], // Include any nested folders if present
            })),

            // Add treatment plans as children
            ...treatments.map((plan: any) => ({
              id: plan.id,
              name: plan.name || '',
              type: 'treatment',
              tasks: plan.task_count || 0,
              uses: plan.usage_count || 0,
              date_created: plan.create_date || new Date().toISOString(),
            })),
          ],
        };

        // Return the hierarchical structure
        console.log('Transformed to hierarchical structure:', rootFolder);
        return {
          success: true,
          data: rootFolder,
        };
      }

      // For any other format, just return the data as is
      return response;
    }

    return response;
  }

  static async getById(id: string): Promise<ApiResponse<TreatmentPlan>> {
    console.log('TreatmentPlanApi.getById called with ID:', id);
    const planResponse = await this.request<any>(`/treatment-plans/${id}/`);

    if (!planResponse.success) {
      console.error('Failed to get treatment plan:', planResponse.error);
      return planResponse;
    }

    const planData = planResponse.data;
    console.log('Treatment plan raw response:', planData);

    // The backend might now be returning the steps directly in the response
    let steps = [];

    // If the backend already included steps in the response, use them
    if (planData.steps && Array.isArray(planData.steps)) {
      console.log('Steps already included in the treatment plan response', planData.steps);
      steps = planData.steps;
    } else {
      // Fetch steps for the treatment plan if not included
      console.log('Steps not included in response, fetching from endpoint');
      const stepsResponse = await this.request<any[]>(`/treatment-plans/${id}/steps/`);

      if (!stepsResponse.success) {
        console.error('Failed to fetch steps:', stepsResponse.error);
        return {
          success: false,
          error: stepsResponse.error || 'Failed to fetch treatment plan steps',
        };
      }

      console.log('Steps fetched successfully:', stepsResponse.data);
      steps = stepsResponse.data || [];
    }

    // Calculate progress
    const completedSteps = steps.filter((step: any) => step.complete).length;
    const progress = steps.length > 0 ? Math.round((completedSteps / steps.length) * 100) : 0;

    // Transform the Django response to match our frontend TreatmentPlan type
    const treatmentPlan: TreatmentPlan = {
      id: planData.id?.toString() || '',
      name: planData.name || '',
      description: planData.description || '',
      patientId: planData.patient?.toString() || '',
      dateCreated: planData.create_time
        ? new Date(planData.create_time).toISOString().split('T')[0]
        : new Date().toISOString().split('T')[0],
      dateModified: planData.update_time
        ? new Date(planData.update_time).toISOString().split('T')[0]
        : new Date().toISOString().split('T')[0],
      status: 'active',
      completedSteps: completedSteps,
      totalSteps: steps.length,
      // Return steps as an array for easier use
      steps: steps,
      notes: '',
      createdBy: planData.create_by || '',
      folder: planData.folder?.toString() || '',
      assignedPatients: (planData.assigned_patients || []).map((id: number) => String(id)),
    };

    console.log('Transformed treatment plan:', treatmentPlan);
    return { success: true, data: treatmentPlan };
  }

  static async getByPatient(patientId: string): Promise<ApiResponse<TreatmentPlan[]>> {
    try {
      // Based on the backend API, we should find treatment plans in patient.treatment_plans
      const patientResponse = await this.request<any>(`/patients/${patientId}/`);

      if (!patientResponse.success || !patientResponse.data) {
        return patientResponse;
      }

      const patientData = patientResponse.data;

      // Look for treatment_plans array from the patient response
      // based on the PatientViewSet.list response format
      if (!patientData.treatment_plans && !Array.isArray(patientData.treatment_plans)) {
        return { success: true, data: [] };
      }

      // Transform the treatment_plans from the patient data to the frontend format
      const treatmentPlansData = patientData.treatment_plans || [];

      // We might need to make additional requests to get the full treatment plan details
      const treatmentPlans = await Promise.all(
        treatmentPlansData.map(async (planData: any) => {
          // If we have the full plan details already, use them
          if (planData.id) {
            // Fetch the complete plan to get steps
            const planResponse = await this.getById(planData.id);
            return planResponse.success ? planResponse.data : null;
          }
          return null;
        })
      );

      // Filter out null values
      const validTreatmentPlans = treatmentPlans.filter(plan => plan !== null) as TreatmentPlan[];

      return { success: true, data: validTreatmentPlans };
    } catch (error) {
      console.error('Error fetching patient treatment plans:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch patient treatment plans',
      };
    }
  }

  static async create(plan: Omit<TreatmentPlan, 'id'>): Promise<ApiResponse<{ id: string }>> {
    try {
      // Parse steps from JSON string
      let steps = [];
      try {
        // If steps is a JSON string, parse it
        if (typeof plan.steps === 'string') {
          steps = JSON.parse(plan.steps);
        } else if (Array.isArray(plan.steps)) {
          // If it's already an array, use it directly
          steps = plan.steps;
        }
      } catch (e) {
        console.error('Error parsing steps from JSON string:', e);
        steps = [];
      }

      // Transform frontend treatment plan to match Django backend format
      const backendPlan = {
        name: plan.name,
        description: plan.description || '',
        patient: plan.patientId || null,
        folder: plan.folder || null, // Make sure folder is included
        // Format steps for the backend if available
        steps: steps.map((step: any, index: number) => ({
          title: step.title,
          description: step.description || '',
          start_date: step.startDate,
          end_date: step.endDate,
          step_num: step.stepNum || index + 1,
          complete: step.completed || false,
          note: step.note || '',
          folder: step.folder || null,
          audio: step.audioId || null,
          media: step.mediaId || null,
        })),
      };

      // If we have patients to assign, include them
      if (plan.patientId) {
        backendPlan.assign_patients = [plan.patientId];
      }

      const response = await this.request<any>('/treatment-plans/', 'POST', backendPlan);

      if (response.success && response.data && response.data.id) {
        return {
          success: true,
          data: {
            id: response.data.id.toString(),
          },
        };
      }

      return {
        success: false,
        error: 'Failed to create treatment plan',
      };
    } catch (error) {
      console.error('Error creating treatment plan:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'An unknown error occurred',
      };
    }
  }

  // static async update(id: string, plan: Partial<TreatmentPlan>): Promise<ApiResponse<void>> {
  //   try {
  //     // Transform frontend treatment plan to match Django backend format
  //     const backendPlan: any = {};

  //     // Include basic properties if they exist
  //     if (plan.name) backendPlan.name = plan.name;
  //     if (plan.description !== undefined) backendPlan.description = plan.description;
  //     if (plan.patientId) backendPlan.patient = plan.patientId;
  //     if (plan.folder) backendPlan.folder = plan.folder;

  //     // Update the treatment plan
  //     const response = await this.request<void>(`/treatment-plans/${id}/`, 'PATCH', backendPlan);

  //     if (!response.success) {
  //       return response;
  //     }

  //     // Update steps if provided
  //     if (plan.steps) {
  //       let steps = [];
  //       try {
  //         // Parse steps if it's a string
  //         if (typeof plan.steps === 'string') {
  //           steps = JSON.parse(plan.steps);
  //         } else if (Array.isArray(plan.steps)) {
  //           steps = plan.steps;
  //         }

  //         // Update or create each step
  //         for (const step of steps) {
  //           const stepData = {
  //             title: step.title,
  //             description: step.description || '',
  //             start_date: step.startDate,
  //             end_date: step.endDate,
  //             step_num: step.order || step.id, // Use ID as fallback for order
  //             complete: step.completed || false,
  //             note: step.note || '',
  //             folder: step.folder || null,
  //             audio: step.audioId || null,
  //             media: step.mediaId || null
  //           };

  //           if (step.id) {
  //             // Update existing step
  //             await this.request(
  //               `/treatment-plans/${id}/steps/${step.id}/`,
  //               'PUT',
  //               stepData
  //             );
  //           } else {
  //             // Create new step
  //             await this.request(
  //               `/treatment-plans/${id}/steps/`,
  //               'POST',
  //               stepData
  //             );
  //           }
  //         }
  //       } catch (e) {
  //         console.error('Error updating steps:', e);
  //         return {
  //           success: false,
  //           error: 'Failed to update steps'
  //         };
  //       }
  //     }

  //     return { success: true };
  //   } catch (error) {
  //     console.error('Error updating treatment plan:', error);
  //     return {
  //       success: false,
  //       error: error instanceof Error ? error.message : 'An unknown error occurred'
  //     };
  //   }
  // }

  static async update(id: string, plan: Partial<TreatmentPlan>): Promise<ApiResponse<void>> {
    try {
      const backendPlan: any = {};

      // Basic fields
      if (plan.name) backendPlan.name = plan.name;
      backendPlan.description = plan.description ?? 'placeholder';
      if (plan.folder) backendPlan.folder = plan.folder;
      if (plan.tags) backendPlan.tags = plan.tags;
      console.log('Updat:', JSON.stringify(plan));

      // Handle nested steps
      if (plan.steps) {
        let steps: any[] = [];

        // Parse steps if they're provided as a string
        if (typeof plan.steps === 'string') {
          steps = JSON.parse(plan.steps);
        } else if (Array.isArray(plan.steps)) {
          steps = plan.steps;
        }

        backendPlan.steps = steps.map((step, index) => {
          // Create the transformed step object
          const transformedStep = {
            title: step.title,
            description: step.description || '',
            start_date: null,
            end_date: null,
            step_num: step.stepNum ?? index + 1, // Use stepNum directly, fallback to index
            complete: step.completed ?? false,
            note: step.dateSelection?.note ?? '',
            folder: step.folder ?? null,
            audio: step.audioId ?? null,
            media: step.mediaId ?? null,
          };

          // Extract dates from dateSelection structure if present
          if (
            step.dateSelection &&
            step.dateSelection.type === 'date-range' &&
            step.dateSelection.dateRange
          ) {
            // Format start_date if not null (YYYY-MM-DD)
            if (step.dateSelection.dateRange.startDate) {
              transformedStep.start_date = new Date(step.dateSelection.dateRange.startDate)
                .toISOString()
                .split('T')[0]; // Convert to YYYY-MM-DD
            }

            // Format end_date if not null (YYYY-MM-DD)
            if (step.dateSelection.dateRange.endDate) {
              transformedStep.end_date = new Date(step.dateSelection.dateRange.endDate)
                .toISOString()
                .split('T')[0]; // Convert to YYYY-MM-DD
            }
          }

          return transformedStep;
        });
      }

      console.log('Sending to backend:', JSON.stringify(backendPlan));

      // Single PUT request with nested steps
      const response = await this.request<void>(`/treatment-plans/${id}/`, 'PUT', backendPlan);

      return response.success
        ? { success: true }
        : { success: false, error: response.error ?? 'Failed to update treatment plan' };
    } catch (error) {
      console.error('Error updating treatment plan:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'An unknown error occurred',
      };
    }
  }

  static async delete(id: string): Promise<ApiResponse<void>> {
    return this.request<void>(`/treatment-plans/${id}/`, 'DELETE');
  }
}

// Step API service for direct step operations
export class StepApi extends BaseApi {
  static async getAll(): Promise<ApiResponse<any>> {
    try {
      const response = await this.request<any>(`/steps/`);

      if (response.success && response.data) {
        console.log('Steps API response:', response.data);
        return { success: true, data: response.data };
      }

      return { success: false, error: 'Failed to retrieve steps' };
    } catch (error) {
      console.error('Error fetching steps:', error);
      return { success: false, error: 'Error fetching steps' };
    }
  }

  static async getByTreatmentPlan(treatmentPlanId: string): Promise<ApiResponse<any[]>> {
    try {
      const response = await this.request<any[]>(`/treatment-plans/${treatmentPlanId}/steps/`);

      if (response.success && response.data) {
        console.log('Steps API response:', response.data);

        // Transform backend steps to match frontend Step type
        const steps = response.data.map((step: any) => ({
          id: step.id?.toString() || '',
          title: step.title || '',
          description: step.description || '',
          startDate: step.start_date || '',
          endDate: step.end_date || '',
          completed: step.complete || false,
          dateCompleted: step.complete ? step.update_time || new Date().toISOString() : undefined,
          treatmentPlanId: treatmentPlanId,
          stepNum: step.step_num || 0,
          note: step.note || '',
          folder: step.folder?.toString() || '',
          audioId: step.audio?.toString(),
          mediaId: step.media?.toString(),
        }));

        return { success: true, data: steps };
      }

      return { success: false, error: 'Failed to retrieve steps' };
    } catch (error) {
      console.error('Error fetching steps:', error);
      return { success: false, error: 'Error fetching steps' };
    }
  }

  static async getById(treatmentPlanId: string, stepId: string): Promise<ApiResponse<any>> {
    try {
      const response = await this.request<any>(
        `/treatment-plans/${treatmentPlanId}/steps/${stepId}/`
      );

      if (response.success && response.data) {
        const step = response.data;

        return {
          success: true,
          data: {
            id: step.id?.toString() || '',
            title: step.title || '',
            description: step.description || '',
            startDate: step.start_date || '',
            endDate: step.end_date || '',
            completed: step.complete || false,
            dateCompleted: step.complete ? step.update_time || new Date().toISOString() : undefined,
            treatmentPlanId: treatmentPlanId,
            stepNum: step.step_num || 0,
            note: step.note || '',
            folder: step.folder?.toString() || '',
            audioId: step.audio?.toString(),
            mediaId: step.media?.toString(),
          },
        };
      }

      return { success: false, error: 'Step not found' };
    } catch (error) {
      console.error('Error fetching step by ID:', error);
      return { success: false, error: 'Error fetching step details' };
    }
  }

  static async create(treatmentPlanId: string, step: any): Promise<ApiResponse<any>> {
    try {
      const stepData = {
        title: step.title,
        description: step.description || '',
        start_date: step.startDate,
        end_date: step.endDate,
        step_num: step.stepNum || 0,
        complete: step.completed || false,
        note: step.note || '',
        folder: step.folder || null,
        audio: step.audioId || null,
        media: step.mediaId || null,
        treatment: treatmentPlanId,
      };

      const response = await this.request<any>(
        `/treatment-plans/${treatmentPlanId}/steps/`,
        'POST',
        stepData
      );

      if (response.success && response.data) {
        return {
          success: true,
          data: {
            ...response.data,
            id: response.data.id?.toString(),
            treatmentPlanId,
          },
        };
      }

      return { success: false, error: 'Failed to create step' };
    } catch (error) {
      console.error('Error creating step:', error);
      return { success: false, error: 'Error creating step' };
    }
  }

  static async saveLibraryStep(step: any): Promise<ApiResponse<any>> {
    try {
      const response = await this.request<any>(`/steps/`, 'POST', step);

      if (response.success && response.data) {
        return { success: true, data: response.data };
      }

      return { success: false, error: 'Failed to save step to library' };
    } catch (error) {
      console.error('Error saving step to library:', error);
      return { success: false, error: 'Error saving step to library' };
    }
  }

  static async update(
    treatmentPlanId: string,
    stepId: string,
    step: any
  ): Promise<ApiResponse<any>> {
    try {
      const stepData = {
        title: step.title,
        description: step.description || '',
        start_date: step.startDate,
        end_date: step.endDate,
        step_num: step.stepNum || 0,
        complete: step.completed || false,
        note: step.note || '',
        folder: step.folder || null,
        audio: step.audioId || null,
        media: step.mediaId || null,
      };

      const response = await this.request<any>(
        `/treatment-plans/${treatmentPlanId}/steps/${stepId}/`,
        'PUT',
        stepData
      );

      if (response.success && response.data) {
        return {
          success: true,
          data: {
            ...response.data,
            id: response.data.id?.toString(),
            treatmentPlanId,
          },
        };
      }

      return { success: false, error: 'Failed to update step' };
    } catch (error) {
      console.error('Error updating step:', error);
      return { success: false, error: 'Error updating step' };
    }
  }

  static async delete(treatmentPlanId: string, stepId: string): Promise<ApiResponse<void>> {
    return this.request<void>(`/treatment-plans/${treatmentPlanId}/steps/${stepId}/`, 'DELETE');
  }
}

// Create a backward-compatible general API service
class ApiServiceClass {
  async get<T>(url: string): Promise<ApiResponse<T>> {
    return BaseApi.request<T>(url);
  }

  async post<T>(url: string, data: Record<string, any>): Promise<ApiResponse<T>> {
    return BaseApi.request<T>(url, 'POST', data);
  }

  async put<T>(url: string, data: Record<string, any>): Promise<ApiResponse<T>> {
    return BaseApi.request<T>(url, 'PUT', data);
  }

  async delete<T>(url: string): Promise<ApiResponse<T>> {
    return BaseApi.request<T>(url, 'DELETE');
  }
}

// Create a single instance to use throughout the app
export const ApiService = new ApiServiceClass();

// Export medication and appointment APIs with similar structure to maintain compatibility
export class MedicationApi extends BaseApi {
  static async getByPatient(patientId: string): Promise<ApiResponse<Medication[]>> {
    const response = await this.request<any>(`/patients/${patientId}/`);

    if (!response.success) {
      return response;
    }

    const patientData = response.data;

    // Check if patient has medication info
    if (!patientData.medical_info || !patientData.medical_info.cur_medications) {
      return { success: true, data: [] };
    }

    // Convert raw medication string to structured data
    // This is a simplification; real implementation would parse the backend data structure
    const medicationText = patientData.medical_info.cur_medications || '';
    const medications: Medication[] = medicationText
      .split(',')
      .map((med: string, index: number) => ({
        id: `med-${patientId}-${index}`,
        patientId,
        name: med.trim(),
        dosage: '',
        frequency: '',
        prescribedDate: new Date().toISOString(),
        status: 'active',
        reason: '',
        instructions: '',
        sideEffects: [],
        prescribedBy: '',
        refills: 0,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      }));

    return { success: true, data: medications };
  }
}

export class AppointmentApi {
  // Implementation will be added when the Django backend has appointment endpoints
  // For now, we'll return an empty array
  static async getByPatient(patientId: string): Promise<ApiResponse<Appointment[]>> {
    return { success: true, data: [] };
  }
}

// Export a Template API to maintain compatibility
export class TemplateApi {
  static async getAll(): Promise<ApiResponse<any[]>> {
    return { success: true, data: [] };
  }
}

// Folder API service
export class FolderApi extends BaseApi {
  static async getAll(): Promise<ApiResponse<any>> {
    try {
      const response = await this.request<any>('/folders/');

      if (response.success && response.data) {
        // The backend returns a tree structure directly
        // We'll keep it as is and let the UI components handle the rendering
        return { success: true, data: response.data };
      }

      return { success: false, error: 'Failed to retrieve folders' };
    } catch (error) {
      console.error('Error fetching folders:', error);
      return { success: false, error: 'Error fetching folders' };
    }
  }

  static async getById(id: string): Promise<ApiResponse<any>> {
    try {
      const response = await this.request<any>(`/folders/${id}/`);

      if (response.success && response.data) {
        return { success: true, data: response.data };
      }

      return { success: false, error: 'Folder not found' };
    } catch (error) {
      console.error('Error fetching folder by ID:', error);
      return { success: false, error: 'Error fetching folder details' };
    }
  }

  static async create(name: string, parentId?: string): Promise<ApiResponse<any>> {
    try {
      const folderData: any = { name };

      // Include parent_id if provided
      if (parentId) {
        folderData.parent_id = parentId;
      }

      const response = await this.request<any>('/folders/', 'POST', folderData);

      if (response.success && response.data) {
        return { success: true, data: response.data };
      }

      return { success: false, error: 'Failed to create folder' };
    } catch (error) {
      console.error('Error creating folder:', error);
      return { success: false, error: 'Error creating folder' };
    }
  }

  static async update(id: string, name: string, parentId?: string): Promise<ApiResponse<any>> {
    try {
      const folderData: any = { name };

      // Include parent if provided
      if (parentId) {
        folderData.parent = parentId;
      }

      const response = await this.request<any>(`/folders/${id}/`, 'PATCH', folderData);

      if (response.success && response.data) {
        return { success: true, data: response.data };
      }

      return { success: false, error: 'Failed to update folder' };
    } catch (error) {
      console.error('Error updating folder:', error);
      return { success: false, error: 'Error updating folder' };
    }
  }

  static async delete(id: string): Promise<ApiResponse<void>> {
    return this.request<void>(`/folders/${id}/`, 'DELETE');
  }

  // Find a treatment plan by ID in the folder tree recursively
  static findTreatmentInTree(tree: any, treatmentId: string): any {
    if (tree.type === 'treatment' && tree.id.toString() === treatmentId) {
      return tree;
    }

    if (tree.children && tree.children.length > 0) {
      for (const child of tree.children) {
        const found = this.findTreatmentInTree(child, treatmentId);
        if (found) return found;
      }
    }

    return null;
  }

  // Find a folder by ID in the folder tree recursively
  static findFolderInTree(tree: any, folderId: string): any {
    if (tree.type === 'folder' && tree.id.toString() === folderId) {
      return tree;
    }

    if (tree.children && tree.children.length > 0) {
      for (const child of tree.children) {
        const found = this.findFolderInTree(child, folderId);
        if (found) return found;
      }
    }

    return null;
  }
}

// Media API service
export class MediaApi extends BaseApi {
  static async getAll(): Promise<ApiResponse<any[]>> {
    try {
      const response = await this.request<any>('/media/');

      if (response.success && response.data) {
        console.log('Media API response:', response.data);

        // The backend returns a folder tree structure with media items
        // Extract all media items recursively from the tree
        const mediaItems = this.extractMediaItems(response.data);

        return { success: true, data: mediaItems };
      }

      return { success: false, error: 'Failed to retrieve media' };
    } catch (error) {
      console.error('Error fetching media:', error);
      return { success: false, error: 'Error fetching media' };
    }
  }

  // Recursively extract media items from folder structure
  private static extractMediaItems(folderTree: any): any[] {
    let mediaItems: any[] = [];

    if (!folderTree || !folderTree.children) {
      return mediaItems;
    }

    // Get media items at this level
    const items = folderTree.children.filter((item: any) => item.type === 'media');

    // Transform media items
    const transformedItems = items.map((item: any) => {
      // Determine media type based on media_type field or file extension
      let mediaType: 'pdf' | 'image' | 'video' | 'youtube' | 'audio' = 'image'; // Default

      if (item.media_type) {
        // Map Django media types to frontend types
        switch (item.media_type.toUpperCase()) {
          case 'DOC':
            mediaType = 'pdf';
            break;
          case 'IMAGE':
            mediaType = 'image';
            break;
          case 'VIDEO':
            mediaType = 'video';
            break;
          case 'AUDIO':
            mediaType = 'audio';
            break;
          default:
            mediaType = 'pdf';
        }
      } else if (item.file) {
        // Try to determine from file extension
        const ext = item.file.split('.').pop()?.toLowerCase();
        if (ext === 'pdf' || ext === 'doc' || ext === 'docx' || ext === 'txt') {
          mediaType = 'pdf';
        } else if (ext === 'jpg' || ext === 'jpeg' || ext === 'png' || ext === 'gif') {
          mediaType = 'image';
        } else if (ext === 'mp4' || ext === 'avi' || ext === 'mov') {
          mediaType = 'video';
        } else if (ext === 'mp3' || ext === 'wav' || ext === 'ogg') {
          mediaType = 'audio';
        }
      }

      return {
        id: item.id.toString(),
        name: item.name || 'Untitled',
        type: mediaType,
        url: item.file || '',
        thumbnail: mediaType === 'image' ? item.file : undefined,
        dateAdded: item.create_time
          ? new Date(item.create_time).toLocaleDateString()
          : 'Unknown date',
        folderId: folderTree.id.toString(),
        // Additional fields might not be available from the backend
        size: '',
        duration: '',
      };
    });

    mediaItems = [...mediaItems, ...transformedItems];

    // Recursively get media items from subfolders
    const subfolders = folderTree.children.filter((item: any) => item.type === 'folder');
    for (const subfolder of subfolders) {
      mediaItems = [...mediaItems, ...this.extractMediaItems(subfolder)];
    }

    return mediaItems;
  }

  static async getById(id: string): Promise<ApiResponse<any>> {
    try {
      const response = await this.request<any>(`/media/${id}/`);

      if (response.success && response.data) {
        const item = response.data;

        // Determine media type based on media_type field or file extension
        let mediaType: 'pdf' | 'image' | 'video' | 'youtube' | 'audio' = 'image'; // Default

        if (item.media_type) {
          switch (item.media_type.toUpperCase()) {
            case 'DOC':
              mediaType = 'pdf';
              break;
            case 'IMAGE':
              mediaType = 'image';
              break;
            case 'VIDEO':
              mediaType = 'video';
              break;
            case 'AUDIO':
              mediaType = 'audio';
              break;
            default:
              mediaType = 'pdf';
          }
        }

        const mediaItem = {
          id: item.id.toString(),
          name: item.title || 'Untitled',
          type: mediaType,
          url: item.file || '',
          thumbnail: mediaType === 'image' ? item.file : undefined,
          dateAdded: item.create_time
            ? new Date(item.create_time).toLocaleDateString()
            : 'Unknown date',
          size: '',
          duration: '',
        };

        return { success: true, data: mediaItem };
      }

      return { success: false, error: 'Media not found' };
    } catch (error) {
      console.error('Error fetching media by ID:', error);
      return { success: false, error: 'Error fetching media details' };
    }
  }

  static async upload(file: File, title?: string): Promise<ApiResponse<any>> {
    try {
      // Create a FormData object to send the file
      const formData = new FormData();
      formData.append('file', file);
      formData.append('title', title || file.name);

      // Determine media type from file type
      let mediaType = 'DOC'; // Default
      if (file.type.startsWith('image/')) {
        mediaType = 'IMAGE';
      } else if (file.type.startsWith('video/')) {
        mediaType = 'VIDEO';
      } else if (file.type.startsWith('audio/')) {
        mediaType = 'AUDIO';
      }

      formData.append('media_type', mediaType);

      // Use axiosInstance which has the interceptor for the auth token.
      const response = await axiosInstance.post(`/media/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error uploading media:', error);
      return { success: false, error: 'Error uploading media' };
    }
  }

  static async delete(id: string): Promise<ApiResponse<void>> {
    return this.request<void>(`/media/${id}/`, 'DELETE');
  }
}

// Analytics API service
export class AnalyticsApi extends BaseApi {
  static async getPatientProgress(): Promise<ApiResponse<any[]>> {
    try {
      // This endpoint might not exist yet in the backend
      const response = await this.request<any[]>('/analytics/patient-progress/');

      if (response.success && response.data) {
        return response;
      }

      // If the endpoint doesn't exist or returns no data, we'll fetch patients and format them
      const patientsResponse = await PatientApi.getAll();

      if (patientsResponse.success && patientsResponse.data) {
        // Transform patient data into progress format
        const progressData = patientsResponse.data.map((patient: any) => {
          const treatmentPlans = patient.treatment_plans || [];

          // Calculate progress based on available data
          let totalSteps = 0;
          let completedSteps = 0;
          let planName = 'General Care Plan';

          if (treatmentPlans.length > 0) {
            planName = treatmentPlans[0].name;

            if (treatmentPlans[0].completion) {
              const [completed, total] = treatmentPlans[0].completion.split('/').map(Number);
              completedSteps = completed || 0;
              totalSteps = total || 0;
            }
          }

          // Calculate progress percentage
          const progress = totalSteps > 0 ? Math.round((completedSteps / totalSteps) * 100) : 0;

          // Determine status based on progress
          let status: 'on-track' | 'at-risk' | 'completed' = 'on-track';
          if (progress === 100) {
            status = 'completed';
          } else if (progress < 30) {
            status = 'at-risk';
          }

          return {
            id: patient.id,
            name: `${patient.firstName} ${patient.lastName}`.trim(),
            plan: planName,
            startDate: patient.dateAdded || new Date().toLocaleDateString(),
            status: status,
            progress: progress,
            completedSteps: completedSteps,
            totalSteps: totalSteps || 10, // Default if not available
            lastActive: patient.lastVisit || '1 day ago',
          };
        });

        return { success: true, data: progressData };
      }

      return { success: false, error: 'Failed to retrieve patient progress data' };
    } catch (error) {
      console.error('Error fetching patient progress:', error);
      return { success: false, error: 'Error fetching analytics data' };
    }
  }

  static async getTreatmentInsights(): Promise<ApiResponse<any>> {
    try {
      // This endpoint might not exist yet in the backend
      const response = await this.request<any>('/analytics/treatment-insights/');

      if (response.success && response.data) {
        return response;
      }

      // If the endpoint doesn't exist, we'll return mock data for now
      // This can be replaced with actual calculations based on patient data later
      const mockInsights = {
        averagePlanAdherence: {
          value: 82,
          change: 8,
        },
        medicationAdherence: {
          value: 91,
          change: 5,
        },
        qualityOfLifeScore: {
          value: 4.2,
          change: 0.3,
        },
        effectivePlans: [
          { name: 'Diabetes Management Protocol', effectiveness: 94 },
          { name: 'Arthritis Management Program', effectiveness: 91 },
          { name: 'Fall Prevention Program', effectiveness: 89 },
          { name: 'Medication Management Plan', effectiveness: 87 },
        ],
        improvementAreas: [
          {
            title: 'Medication Schedule Adherence',
            description:
              'Only 75% of patients follow medication schedules strictly. Consider implementing medication reminders and simplified schedules.',
            impact: 'high',
          },
          {
            title: 'Exercise Program Participation',
            description:
              '60% of patients complete recommended exercises. Review exercise complexity and consider adding more seated/supported options.',
            impact: 'medium',
          },
          {
            title: 'Blood Sugar Monitoring',
            description:
              '30% of diabetic patients miss regular monitoring. Evaluate monitoring device usability and provide additional training.',
            impact: 'high',
          },
        ],
      };

      return { success: true, data: mockInsights };
    } catch (error) {
      console.error('Error fetching treatment insights:', error);
      return { success: false, error: 'Error fetching insights data' };
    }
  }
}
